import React, { useEffect, useState, useRef } from "react";
import "./UserLoginActivity.css";

const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

const UserLoginActivity = () => {
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // ---------------------------------------------------------
    // PAGINATION
    // ---------------------------------------------------------
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(20);

    // ---------------------------------------------------------
    // FILTER STATES (LIVE / AUTOMATIC)
    // ---------------------------------------------------------
    const [userSearch, setUserSearch] = useState("");
    const [organizationSearch, setOrganizationSearch] = useState("");

    // ---------------------------------------------------------
    // DATE RANGE & CALENDAR POPUP STATE
    // ---------------------------------------------------------
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState("last30days");

    // Active calculated date strings (YYYY-MM-DD)
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");

    // Temporary selection inside calendar popup
    const [tempStartDate, setTempStartDate] = useState("");
    const [tempEndDate, setTempEndDate] = useState("");

    // Month view navigation state
    const [calendarViewDate, setCalendarViewDate] = useState(new Date());

    const datePickerRef = useRef(null);

    // ---------------------------------------------------------
    // HELPER: FORMAT JS DATE -> YYYY-MM-DD
    // ---------------------------------------------------------
    const formatDateForInput = (date) => {
        if (!date) return "";
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // ---------------------------------------------------------
    // HELPER: FORMAT DISPLAY RANGE (DD/MM/YYYY - DD/MM/YYYY)
    // ---------------------------------------------------------
    const formatDisplayRange = (startStr, endStr) => {
        if (!startStr || !endStr) return "Select Date Range";
        const formatStr = (str) => {
            const [y, m, d] = str.split("-");
            return `${d}/${m}/${y}`;
        };
        return `${formatStr(startStr)} - ${formatStr(endStr)}`;
    };

    // ---------------------------------------------------------
    // CALCULATE PRESET DATES
    // ---------------------------------------------------------
    const calculatePresetDates = (preset) => {
        const today = new Date();
        const todayStr = formatDateForInput(today);

        if (preset === "today") {
            return { start: todayStr, end: todayStr };
        }
        if (preset === "yesterday") {
            const yest = new Date();
            yest.setDate(yest.getDate() - 1);
            const yestStr = formatDateForInput(yest);
            return { start: yestStr, end: yestStr };
        }
        if (preset === "last7days") {
            const start = new Date();
            start.setDate(start.getDate() - 6);
            return { start: formatDateForInput(start), end: todayStr };
        }
        if (preset === "last30days") {
            const start = new Date();
            start.setDate(start.getDate() - 29);
            return { start: formatDateForInput(start), end: todayStr };
        }
        if (preset === "thisMonth") {
            const start = new Date(today.getFullYear(), today.getMonth(), 1);
            return { start: formatDateForInput(start), end: todayStr };
        }
        if (preset === "lastMonth") {
            const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const end = new Date(today.getFullYear(), today.getMonth(), 0);
            return { start: formatDateForInput(start), end: formatDateForInput(end) };
        }
        return { start: startDate, end: endDate };
    };

    // Initialize with Last 30 Days
    useEffect(() => {
        const { start, end } = calculatePresetDates("last30days");
        setStartDate(start);
        setEndDate(end);
        setTempStartDate(start);
        setTempEndDate(end);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Close calendar on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
                setShowDatePicker(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // ---------------------------------------------------------
    // GET CURRENT USER & PERMISSION CHECK
    // ---------------------------------------------------------
    const getCurrentUser = () => {
        try {
            const storedUser = localStorage.getItem("user") || localStorage.getItem("localUser");
            return storedUser ? JSON.parse(storedUser) : null;
        } catch (error) {
            console.error("Unable to read current user:", error);
            return null;
        }
    };

    const currentUser = getCurrentUser();
    const orgRole = currentUser?.org_role?.toUpperCase() || "";
    const organizationType = currentUser?.organization_type?.toUpperCase() || "";
    const isAllowedUser = organizationType === "INTERNAL" && orgRole === "ADMIN";

    // ---------------------------------------------------------
    // FORMAT DATE/TIME FOR TABLE USING RECORD'S TIMEZONE
    // ---------------------------------------------------------
    const formatDateTime = (value, timezone) => {
        if (!value) {
            return "—";
        }

        let dateValue = value;

        // MySQL DATETIME is stored as UTC but does not contain timezone information.
        // Explicitly mark it as UTC before converting to the record's timezone.
        if (typeof value === "string") {
            const trimmedValue = value.trim();

            if (
                !trimmedValue.endsWith("Z") &&
                !/[+-]\d{2}:\d{2}$/.test(trimmedValue)
            ) {
                dateValue = `${trimmedValue.replace(" ", "T")}Z`;
            }
        }

        const date = new Date(dateValue);

        if (Number.isNaN(date.getTime())) {
            return "—";
        }

        const safeTimezone =
            typeof timezone === "string" &&
            timezone.trim() &&
            !["undefined", "null", "none", "unknown"].includes(
                timezone.trim().toLowerCase()
            )
                ? timezone.trim()
                : "UTC";

        return date.toLocaleString("en-IN", {
            timeZone: safeTimezone,
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: true,
        });
    };

    // ---------------------------------------------------------
    // FETCH LOGIN ACTIVITY
    // ---------------------------------------------------------
    const fetchLoginActivity = async () => {
        try {
            setLoading(true);
            setError("");

            const jwt = localStorage.getItem("jwt");
            if (!jwt) {
                setError("Authentication token not found.");
                return;
            }

            const response = await fetch(`${API_BASE_URL}/api/auth/user-login-activity`, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${jwt}`,
                    "X-User-ID": String(currentUser?.id),
                    "Content-Type": "application/json",
                },
            });

            if (!response.ok) {
                throw new Error(`Failed to fetch login activity (${response.status})`);
            }

            const data = await response.json();
            const items = Array.isArray(data?.items)
                ? data.items
                : Array.isArray(data)
                ? data
                : [];

            setActivities(items);
        } catch (error) {
            console.error("USER LOGIN ACTIVITY ERROR:", error);
            setError(error?.message || "Unable to load user login activity.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowedUser) {
            setLoading(false);
            return;
        }
        fetchLoginActivity();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isAllowedUser]);

    // ---------------------------------------------------------
    // CALENDAR PRESET & DAY SELECTION
    // ---------------------------------------------------------
    const handlePresetSelect = (presetKey) => {
        setSelectedPreset(presetKey);
        if (presetKey !== "custom") {
            const { start, end } = calculatePresetDates(presetKey);
            setStartDate(start);
            setEndDate(end);
            setTempStartDate(start);
            setTempEndDate(end);
            setShowDatePicker(false);
        }
    };

    const handleDayClick = (dateStr) => {
        setSelectedPreset("custom");
        if (!tempStartDate || (tempStartDate && tempEndDate)) {
            setTempStartDate(dateStr);
            setTempEndDate("");
        } else if (tempStartDate && !tempEndDate) {
            if (dateStr < tempStartDate) {
                setTempStartDate(dateStr);
                setTempEndDate("");
            } else {
                setTempEndDate(dateStr);
            }
        }
    };

    const handleApplyCalendar = () => {
        if (tempStartDate) {
            setStartDate(tempStartDate);
            setEndDate(tempEndDate || tempStartDate);
            setShowDatePicker(false);
        }
    };

    const handleReset = () => {
        setUserSearch("");
        setOrganizationSearch("");
        setSelectedPreset("last30days");
        const { start, end } = calculatePresetDates("last30days");
        setStartDate(start);
        setEndDate(end);
        setTempStartDate(start);
        setTempEndDate(end);
        setCurrentPage(1);
    };

    // ---------------------------------------------------------
    // REAL-TIME AUTOMATIC FILTERING
    // ---------------------------------------------------------
    const filteredActivities = activities.filter((item) => {
        // User Search
        const userValue = String(item?.actor_email || item?.user_name || "").toLowerCase();
        const userMatches = userValue.includes(userSearch.trim().toLowerCase());

        // Organization Search
        const orgName = String(item?.organization_name || "").toLowerCase();
        const organizationMatches = orgName.includes(organizationSearch.trim().toLowerCase());

        // Date Filter
        const activityVal = item?.login_at || item?.logout_at;
        let dateMatches = true;
        if (activityVal && startDate && endDate) {
            const actDate = formatDateForInput(new Date(activityVal));
            dateMatches = actDate >= startDate && actDate <= endDate;
        }

        return userMatches && organizationMatches && dateMatches;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [userSearch, organizationSearch, startDate, endDate]);

    // ---------------------------------------------------------
    // CALENDAR MONTH RENDERER
    // ---------------------------------------------------------
    const renderCalendarMonth = (monthOffset) => {
        const year = calendarViewDate.getFullYear();
        const month = calendarViewDate.getMonth() + monthOffset;
        const targetDate = new Date(year, month, 1);
        const displayYear = targetDate.getFullYear();
        const displayMonth = targetDate.getMonth();

        const monthName = targetDate.toLocaleString("en-US", { month: "short" });
        const daysInMonth = new Date(displayYear, displayMonth + 1, 0).getDate();
        const firstDayOfWeek = new Date(displayYear, displayMonth, 1).getDay();

        const days = [];
        for (let i = 0; i < firstDayOfWeek; i++) {
            days.push(<div key={`empty-${i}`} className="cal-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const dateObj = new Date(displayYear, displayMonth, day);
            const dateStr = formatDateForInput(dateObj);

            const isStart = dateStr === tempStartDate;
            const isEnd = dateStr === tempEndDate;
            const isInRange =
                tempStartDate &&
                tempEndDate &&
                dateStr > tempStartDate &&
                dateStr < tempEndDate;

            let dayClass = "cal-day";
            if (isStart || isEnd) dayClass += " selected";
            if (isInRange) dayClass += " in-range";

            days.push(
                <button
                    key={dateStr}
                    type="button"
                    className={dayClass}
                    onClick={() => handleDayClick(dateStr)}
                >
                    {day}
                </button>
            );
        }

        return (
            <div className="calendar-month">
                <div className="calendar-month-header">
                    {monthOffset === 0 && (
                        <button
                            type="button"
                            className="cal-nav-btn"
                            onClick={() =>
                                setCalendarViewDate(
                                    new Date(
                                        calendarViewDate.getFullYear(),
                                        calendarViewDate.getMonth() - 1,
                                        1
                                    )
                                )
                            }
                        >
                            &lt;
                        </button>
                    )}
                    <span className="cal-month-title">{monthName} {displayYear}</span>
                    {monthOffset === 1 && (
                        <button
                            type="button"
                            className="cal-nav-btn"
                            onClick={() =>
                                setCalendarViewDate(
                                    new Date(
                                        calendarViewDate.getFullYear(),
                                        calendarViewDate.getMonth() + 1,
                                        1
                                    )
                                )
                            }
                        >
                            &gt;
                        </button>
                    )}
                </div>
                <div className="cal-weekdays">
                    <span>Su</span><span>Mo</span><span>Tu</span><span>We</span><span>Th</span><span>Fr</span><span>Sa</span>
                </div>
                <div className="cal-days-grid">{days}</div>
            </div>
        );
    };

    // PAGINATION CALCULATIONS
    const totalPages = Math.max(1, Math.ceil(filteredActivities.length / pageSize));
    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * pageSize;
    const paginatedActivities = filteredActivities.slice(startIndex, startIndex + pageSize);

    if (!isAllowedUser) {
        return (
            <div className="user-login-activity-page">
                <div className="user-login-activity-card permission-card">
                    <div className="permission-title">Access Restricted</div>
                    <div className="permission-message">
                        You do not have permission to view user login activity.
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="user-login-activity-page">
            <div className="user-login-activity-container">

                {/* HEADER */}
                <div className="user-login-activity-header">
                    <div>
                        <h1>User Login Activity</h1>
                        <p>View user login and logout activity</p>
                    </div>

                    <button
                        type="button"
                        className="user-login-refresh-btn"
                        onClick={fetchLoginActivity}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>

                {/* FILTER CARD */}
                <div className="user-login-filter-card">

                    {/* 1. CALENDAR DATE RANGE PICKER */}
                    <div className="user-login-filter-group date-picker-relative" ref={datePickerRef}>
                        <label>Date Range</label>
                        <div
                            className="date-picker-trigger-input"
                            onClick={() => setShowDatePicker(!showDatePicker)}
                        >
                            <span>{formatDisplayRange(startDate, endDate)}</span>
                            <span className="calendar-icon">📅</span>
                        </div>

                        {/* CALENDAR POPUP */}
                        {showDatePicker && (
                            <div className="calendar-popup">
                                <div className="calendar-popup-sidebar">
                                    <button
                                        type="button"
                                        className={selectedPreset === "today" ? "active" : ""}
                                        onClick={() => handlePresetSelect("today")}
                                    >
                                        Today
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "yesterday" ? "active" : ""}
                                        onClick={() => handlePresetSelect("yesterday")}
                                    >
                                        Yesterday
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "last7days" ? "active" : ""}
                                        onClick={() => handlePresetSelect("last7days")}
                                    >
                                        Last 7 Days
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "last30days" ? "active" : ""}
                                        onClick={() => handlePresetSelect("last30days")}
                                    >
                                        Last 30 Days
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "thisMonth" ? "active" : ""}
                                        onClick={() => handlePresetSelect("thisMonth")}
                                    >
                                        This Month
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "lastMonth" ? "active" : ""}
                                        onClick={() => handlePresetSelect("lastMonth")}
                                    >
                                        Last Month
                                    </button>
                                    <button
                                        type="button"
                                        className={selectedPreset === "custom" ? "active" : ""}
                                        onClick={() => setSelectedPreset("custom")}
                                    >
                                        Custom Range
                                    </button>
                                </div>

                                <div className="calendar-popup-main">
                                    <div className="calendar-months-container">
                                        {renderCalendarMonth(0)}
                                        {renderCalendarMonth(1)}
                                    </div>
                                    <div className="calendar-popup-footer">
                                        <span className="cal-range-preview">
                                            {formatDisplayRange(tempStartDate, tempEndDate)}
                                        </span>
                                        <div className="cal-footer-actions">
                                            <button
                                                type="button"
                                                className="cal-cancel-btn"
                                                onClick={() => setShowDatePicker(false)}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className="cal-apply-btn"
                                                onClick={handleApplyCalendar}
                                            >
                                                Apply
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* 2. USER NAME / EMAIL SEARCH */}
                    <div className="user-login-filter-group">
                        <label>User / Email</label>
                        <input
                            type="text"
                            value={userSearch}
                            onChange={(e) => setUserSearch(e.target.value)}
                            placeholder="Search user email or name..."
                            className="user-login-filter-input"
                        />
                    </div>

                    {/* 3. ORGANIZATION FILTER */}
                    <div className="user-login-filter-group organization-filter-group">
                        <label>Organization</label>
                        <input
                            type="text"
                            value={organizationSearch}
                            onChange={(e) => setOrganizationSearch(e.target.value)}
                            placeholder="Search organization..."
                            className="user-login-filter-input"
                        />
                    </div>

                    {/* 4. RESET ACTION BUTTON */}
                    <div className="user-login-filter-actions">
                        <button
                            type="button"
                            className="user-login-reset-btn"
                            onClick={handleReset}
                        >
                            Reset
                        </button>
                    </div>

                </div>

                {/* COUNT */}
                <div className="user-login-count">
                    {filteredActivities.length}{" "}
                    {filteredActivities.length === 1 ? "record" : "records"}
                </div>

                {/* ERROR */}
                {error && <div className="user-login-error">{error}</div>}

                {/* TABLE */}
                {loading ? (
                    <div className="user-login-state">Loading login activity...</div>
                ) : (
                    <>
                        <div className="user-login-table-wrapper">
                            <table className="user-login-table">
                                <thead>
                                    <tr>
                                        <th>User Email</th>
                                        <th>Organization</th>
                                        <th>Role</th>
                                        <th>Login Date &amp; Time</th>
                                        <th>Logout Date &amp; Time</th>
                                        <th>IP Address</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginatedActivities.length > 0 ? (
                                        paginatedActivities.map((item, index) => (
                                            <tr
                                                key={
                                                    item?.id ||
                                                    `${item?.actor_email}-${item?.login_at}-${index}`
                                                }
                                            >
                                                <td className="user-email-cell">
                                                    {item?.actor_email || item?.user_name || "—"}
                                                </td>
                                                <td className="user-organization-cell">
                                                    {item?.organization_name || "—"}
                                                </td>
                                                <td className="user-role-cell">
                                                    {item?.org_role || "—"}
                                                </td>
                                                <td>
                                                    {formatDateTime(
                                                        item?.login_at,
                                                        item?.timezone
                                                    )}
                                                </td>
                                                <td>
                                                    {formatDateTime(
                                                        item?.logout_at,
                                                        item?.timezone
                                                    )}
                                                </td>

    <td>
    {item?.ip || "—"}
    </td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan="5" className="user-login-empty">
                                                No login activity found.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* PAGINATION */}
                        {filteredActivities.length > pageSize && (
                            <div className="user-login-pagination">
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                    disabled={safeCurrentPage === 1}
                                >
                                    Previous
                                </button>
                                <span>
                                    Page {safeCurrentPage} of {totalPages}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={safeCurrentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default UserLoginActivity;